/**
 * Iter 31.214a — RadioAdBadge
 *
 * Disclosure pill rendered on radio tracks that carry an `ad_payload`.
 * Shows the `ad_label` from the payload (e.g. "AD · EMERGENT") so
 * listeners always know when a track is sponsored. Auto-fires one
 * impression POST per (track, session) on mount; the title becomes
 * a click-through that routes through `/api/public/radio/ad-click/{id}`
 * before redirecting the listener.
 *
 * Public component — does NOT require auth (mirrors the public-radio
 * stream itself, which is unauthenticated by design).
 *
 * Props:
 *   - track:  the current radio track object with an `ad_payload`
 *             sub-document containing { advertiser_key, click_url,
 *             ad_label?, impression_pixel?, payout_cents? }
 */
import React, { useEffect } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function RadioAdBadge({ track, ...rest }) {
    const ad = track?.ad_payload || {};
    const label = (ad.ad_label || `AD · ${ad.advertiser_key}`).toUpperCase();

    // One impression per (track, browser-session). sessionStorage so a
    // refresh resets the dedupe key — that matches the backend's
    // per-IP-per-hour rate-limit semantics on /ad-impression.
    useEffect(() => {
        if (!track?.id || !ad?.advertiser_key) return;
        const dedupe = `radio_ad_impression:${track.id}`;
        if (sessionStorage.getItem(dedupe)) return;
        sessionStorage.setItem(dedupe, "1");
        axios.post(`${BACKEND}/api/public/radio/ad-impression/${track.id}`, null)
            .catch(() => { /* best-effort; never blocks playback */ });
        // Fire the 3rd-party impression pixel too, if any.
        if (ad.impression_pixel) {
            try {
                const img = new Image();
                img.src = ad.impression_pixel;
            } catch (_) { /* noop */ }
        }
    }, [track?.id, ad?.advertiser_key, ad?.impression_pixel]);

    const handleClick = async (e) => {
        if (!ad?.click_url) return;
        e.preventDefault();
        try {
            // Record the click then redirect via the backend's authoritative URL.
            const { data } = await axios.post(
                `${BACKEND}/api/public/radio/ad-click/${track.id}`,
                null,
            );
            const target = data?.click_url || ad.click_url;
            window.open(target, "_blank", "noopener,noreferrer");
        } catch (_) {
            // If tracking fails, still let the listener through.
            window.open(ad.click_url, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            title={ad.click_url ? "Sponsored — opens advertiser site" : ""}
            className="inline-flex items-center gap-1 mb-1.5 px-1.5 py-0.5 border border-stamp-red text-stamp-red font-heading text-[9px] tracking-[0.22em] uppercase hover:bg-stamp-red hover:text-paper transition-colors cursor-pointer"
            {...rest}
        >
            {label}
        </button>
    );
}
